import { useIntl } from 'react-intl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Heading from 'LuxKit/Typography/Heading'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import React from 'react'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'

interface Props {
  inclusionList: Array<App.OfferInclusion> | Array<App.PackageInclusion>
  heading?: string
  offerURL?: string
  title: string
  luxPlusInclusions?: Array<App.PackageInclusion>
}

function OfferTileInclusionsModal(props: Props) {
  const intl = useIntl()
  const VIEW_OFFER_COPY = intl.formatMessage({
    id: '93IUA-oJOZhorlZeEBd1',
    defaultMessage: 'View offer',
    description: 'CTA button text to view the full offer from the inclusions modal',
  })
  const defaultHeading = intl.formatMessage({
    id: 'X6QCtJ9vaV4HgUtnujZ7',
    defaultMessage: 'Your handpicked inclusions:',
    description: 'Default heading for the inclusions modal listing curated inclusions',
  })
  const {
    inclusionList,
    offerURL,
    title,
    luxPlusInclusions,
    heading = defaultHeading,
  } = props
  const modalElementContext = useModalElementContext<boolean>()

  const bonusInclusions = inclusionList.filter(inclusion => inclusion.isBonus)
  const regularInclusions = inclusionList.filter(inclusion => !inclusion.isBonus)

  const closeModal = (e: React.MouseEvent<HTMLElement>) => {
    const isViewOffer = e.currentTarget.textContent === VIEW_OFFER_COPY
    modalElementContext.resolve(isViewOffer)
  }

  return (<ModalBase size="S" onClose={closeModal}>
    <ModalHeader title={title} onCloseButtonClick={closeModal} />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={8}>
          {!!luxPlusInclusions?.length && (
            <LuxPlusInclusions luxPlusInclusions={luxPlusInclusions} />
          )}
          {!!bonusInclusions.length && (
            <VerticalSpacer gap={4}>
              <PromotionLabel variant="default" format="propercase">{intl.formatMessage({
                id: 'sxoR3qjbGjS9yZkWkUt1',
                defaultMessage: 'Extra inclusions:',
                description: 'Label for bonus inclusions section in inclusions modal',
              })}
              </PromotionLabel>
              <div>
                {bonusInclusions.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
              </div>
            </VerticalSpacer>
          )}
          {!!regularInclusions.length && (
            <VerticalSpacer gap={4}>
              <Heading variant="heading6">{heading}</Heading>
              <div>
                {regularInclusions.map((inclusion) => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
              </div>
            </VerticalSpacer>
          )}
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
    {!!offerURL &&
      <ModalFooter>
        <CSSBreakpoint min="tablet">
          <Group direction="horizontal-reverse">
            <TextButton kind="primary" fit="auto" to={offerURL} onClick={closeModal}>{VIEW_OFFER_COPY}</TextButton>
          </Group>
        </CSSBreakpoint>
        <CSSBreakpoint max="mobile">
          <TextButton kind="primary" fit="full-width" to={offerURL} onClick={closeModal}>{VIEW_OFFER_COPY}</TextButton>
        </CSSBreakpoint>
      </ModalFooter>}
  </ModalBase>)
}

export default OfferTileInclusionsModal
